import { authStore, tokenExpiry } from '../store/authStore';

// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

interface AuthResponse {
  status: string;
  message: string;
  user_id?: number;
  login?: string;
  access_token?: string;
  refresh_token?: string;
  session_data?: any; // Pour MFA
}

// Refus du refresh par l'API (refresh token expiré ou révoqué) : seul cas où l'on déconnecte.
// Une simple panne réseau (portable sorti de veille, wifi coupé) ne doit pas fermer la session.
class RefreshRejectedError extends Error {
  constructor(public readonly status: number) {
    super(`HTTP error! status: ${status}`);
  }
}

// Routes d'authentification : jamais rejouées par l'intercepteur (le refresh y passe lui-même).
const AUTH_ROUTES = ['/login', '/refresh', '/logout', '/logout_refresh', '/auth/'];

// Service de base avec gestion d'erreurs
class AuthService {
  private refreshPromise: Promise<any> | null = null;

  // Login utilisateur
  async loginUser(login: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ login, password }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Soumettre le code MFA
  async submitMfaCode(login: string, password: string, mfaToken: string, sessionData: any): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        login, 
        password,
        mfa_token: mfaToken,
        session_data: sessionData
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // URL de redirection vers le vrai CAS SSO (connexion principale, prod uniquement — voir
  // VITE_AUTH_MODE côté LoginForm)
  async getCasLoginUrl(): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/auth/cas-login-url`);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json.url;
  }

  // Valide le ticket CAS revenu après redirection et obtient les tokens applicatifs
  async validateCasTicket(ticket: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/cas-callback?ticket=${encodeURIComponent(ticket)}`);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }

  // Refresh du token
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    // @jwt_required(refresh=True) côté backend attend le refresh token dans l'en-tête
    // Authorization, pas dans le corps JSON (même convention que logout*Token ci-dessous).
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`
      },
    });

    if (!response.ok) {
      throw new RefreshRejectedError(response.status);
    }

    return response.json();
  }


  // Révoquer uniquement l'access token
  async logoutAccessToken(accessToken: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
    } catch (error) {
      console.error('Logout access token error:', error);
    }
  }

  // Révoquer uniquement le refresh token
  async logoutRefreshToken(refreshToken: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/logout_refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        }
      });
    } catch (error) {
      console.error('Logout refresh token error:', error);
    }
  }

  // Gestion automatique du refresh token
  async handleTokenRefresh(): Promise<string | null> {
    // Éviter les appels multiples simultanés : tous les appelants attendent le même refresh
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const { refreshToken } = authStore.getState();

    if (!refreshToken) {
      return null;
    }

    this.refreshPromise = this.performRefresh(refreshToken);
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(refreshToken: string): Promise<string | null> {
    try {
      authStore.refreshTokenStart();
      const response = await this.refreshToken(refreshToken);
      // TokenRefresh (POST /api/refresh) ne renvoie que {access_token} — pas de champ
      // "status" (contrairement à /login) : ne pas tester response.status === 'ok' ici.
      // Son échéance est lue dans le jeton lui-même (authStore.tokenExpiry).
      if (response.access_token) {
        authStore.refreshTokenSuccess({
          token: response.access_token,
          refreshToken: response.refresh_token ?? '',
        });
        return response.access_token;
      } else {
        throw new RefreshRejectedError(401);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error instanceof RefreshRejectedError) {
        // Refresh token expiré ou révoqué : retour au formulaire de connexion, sur la même page
        authStore.refreshTokenFailure('Session expirée, veuillez vous reconnecter');
      } else {
        // Erreur réseau : on garde la session, la prochaine requête retentera le refresh
        authStore.refreshTokenAborted();
      }
      return null;
    }
  }

  // Intercepteur global de fetch (installé une fois dans main.tsx) : les services reçoivent le
  // jeton en paramètre et une page peut garder en mémoire un jeton périmé. Pour toute requête
  // vers l'API portant un jeton d'accès (hors routes d'authentification) :
  //  - avant l'envoi, un jeton expiré ou sur le point de l'être est remplacé par un jeton valide ;
  //  - si l'API répond 401 malgré tout, on rafraîchit le jeton et on rejoue la requête une fois.
  installFetchInterceptor(): void {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      const sentToken = headers.get('Authorization')?.replace(/^Bearer\s+/i, '');
      const path = url.startsWith(API_BASE_URL) ? url.slice(API_BASE_URL.length) : null;
      const intercept = path !== null
        && !!sentToken
        && tokenExpiry(sentToken) !== null // un JWT de l'application, pas un autre jeton
        && sentToken !== authStore.getState().refreshToken
        && !AUTH_ROUTES.some((route) => path.startsWith(route));

      if (!intercept || input instanceof Request) {
        return originalFetch(input, init);
      }

      const withToken = (token: string): RequestInit => {
        const retryHeaders = new Headers(headers);
        retryHeaders.set('Authorization', `Bearer ${token}`);
        return { ...init, headers: retryHeaders };
      };

      let token = sentToken;
      if (tokenExpiry(sentToken)! <= Date.now() + 30 * 1000) {
        token = (await this.getValidToken()) ?? sentToken;
      }

      const response = await originalFetch(input, withToken(token));
      if (response.status !== 401) {
        return response;
      }

      // Un autre appel a peut-être déjà rafraîchi le jeton pendant ce temps
      const current = authStore.getState().token;
      const freshToken = current && current !== token ? current : await this.handleTokenRefresh();
      return freshToken ? originalFetch(input, withToken(freshToken)) : response;
    };
  }

  // Vérifier si le token expire bientôt (5 min avant expiration)
  isTokenExpiringSoon(): boolean {
    const { tokenExpiresAt } = authStore.getState();

    if (!tokenExpiresAt) return false;

    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return tokenExpiresAt <= fiveMinutesFromNow;
  }

  // Obtenir un token valide (refresh automatique si nécessaire)
  async getValidToken(): Promise<string | null> {
    const { token, isAuthenticated } = authStore.getState();

    if (!isAuthenticated || !token) {
      return null;
    }

    if (this.isTokenExpiringSoon()) {
      // Refresh impossible pour cause réseau : on tente quand même avec le jeton actuel
      return (await this.handleTokenRefresh()) ?? authStore.getState().token;
    }

    return token;
  }
}

export const authService = new AuthService();